import { useI18n } from '@/shared/i18n';
import type { NavV2Section } from '@/shared/navigation/admin-v2-config';
import { ChevronDown } from 'lucide-react';
import { SidebarItem } from './SidebarItem';

interface SidebarSectionProps {
  section: NavV2Section;
  activeId: string;
  badges: Record<string, number>;
  collapsed?: boolean;
  isOpen: boolean;
  onToggle: (id: string) => void;
  onItemClick?: () => void;
}

export function SidebarSection({
  section,
  activeId,
  badges,
  collapsed,
  isOpen,
  onToggle,
  onItemClick,
}: SidebarSectionProps) {
  const { t } = useI18n();

  const containsActive = section.items.some((i) => i.id === activeId);

  if (collapsed) {
    return (
      <div className="mb-2">
        <div className="space-y-0.5">
          {section.items.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              isActive={item.id === activeId}
              badge={item.badgeKey ? badges[item.badgeKey] : undefined}
              collapsed
              onClick={onItemClick}
            />
          ))}
        </div>
        <div className="mx-2 my-2 border-t border-theme-light/60" aria-hidden="true" />
      </div>
    );
  }

  const totalBadge = section.items.reduce(
    (acc, item) => acc + (item.badgeKey ? badges[item.badgeKey] ?? 0 : 0),
    0,
  );

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => onToggle(section.id)}
        aria-expanded={isOpen}
        className={`relative w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors ${
          containsActive
            ? 'text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200'
            : 'text-theme-secondary hover:text-theme'
        }`}
      >
        {containsActive && (
          <span
            className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary-500"
            aria-hidden="true"
          />
        )}

        <span>{t(section.labelKey)}</span>

        <span className="flex items-center gap-1.5 flex-shrink-0">
          {!isOpen && totalBadge > 0 && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" aria-hidden="true" />
          )}
          <ChevronDown
            size={12}
            className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
          />
        </span>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="space-y-0.5 pt-0.5">
            {section.items.map((item) => (
              <SidebarItem
                key={item.id}
                item={item}
                isActive={item.id === activeId}
                badge={item.badgeKey ? badges[item.badgeKey] : undefined}
                onClick={onItemClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
